import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

/**
 * Public lead-capture form. Rendered as a standalone page that can be
 * embedded via <iframe src="…/embed/lead-form"> on Shopify or any website.
 *
 * - No auth required.
 * - Honeypot field ("hp") + basic rate limit on the backend.
 * - Query params supported to prefill: ?service=Full+wrap&vehicle=Tesla+Model+3
 * - Query param ?theme=light|dark (default light for third-party sites).
 */
export default function EmbedLeadFormPage() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");
  const params = new URLSearchParams(window.location.search);
  const theme = params.get("theme") === "dark" ? "dark" : "light";
  const accent = params.get("accent") || "#0F172A";

  // Stable per-visit session id — lets the backend link the abandoned
  // drafts this browser posts to the eventual submit (or leave them as
  // abandoned if the customer never presses Submit). New id per mount
  // so refreshing the form gives you a clean slate.
  const sessionIdRef = React.useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    job_type: "car",
    year: "",
    make: "",
    model: "",
    item_description: "",
    // Legacy composite string, kept as a hidden mirror for backward-compat
    // with old embed integrations. We compose it on submit.
    vehicle: params.get("vehicle") || "",
    registration: "",
    // Multi-select service interest — array of ticked service labels.
    services: params.get("service") ? [params.get("service")] : [],
    notes: "",
    photo_base64: "",
    photo_filename: "",
    photo_preview: "",  // local blob URL for the preview thumbnail
    hp: "", // honeypot — never displayed
  });

  useEffect(() => {
    axios.get(`${API_BASE}/public/lead-form/config`)
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ company_name: "WrapConsole", tagline: "Get a free wrap quote", services: [] }));
  }, []);

  // Abandoned-form tracker: only save a draft when the visitor has typed
  // enough to be a viable follow-up — a name, at least one contact
  // channel (phone or email), and enough vehicle info to know what
  // they're asking about. Anything less is a dead lead not worth
  // chasing. Successful submits will flip the draft to `converted`
  // server-side so it stops showing in the admin Abandoned panel.
  // Skips on the success screen (nothing to draft once we've saved a
  // real lead).
  useEffect(() => {
    if (status === "success" || status === "sending") return;
    const name = (form.name || "").trim();
    const phone = (form.phone || "").trim();
    const email = (form.email || "").trim();
    if (!name) return;
    if (!phone && !email) return;
    const isVehicleType = ["car", "boat", "caravan", "motorhome", "motorbike",
                          "jetski", "commercial", "van", "truck"].includes(form.job_type);
    const year = (form.year || "").trim();
    const make = (form.make || "").trim();
    const model = (form.model || "").trim();
    const item_description = (form.item_description || "").trim();
    const hasVehicle = isVehicleType
      ? (year && make && model) || (make && model)
      : Boolean(item_description);
    if (!hasVehicle) return;
    const t = setTimeout(() => {
      axios.post(`${API_BASE}/public/lead-drafts`, {
        session_id: sessionIdRef.current,
        name: form.name,
        email: form.email,
        phone: form.phone,
        job_type: form.job_type,
        year: form.year,
        make: form.make,
        model: form.model,
        item_description: form.item_description,
        registration: form.registration,
        services: form.services,
        notes: form.notes,
        hp: form.hp,
      }).catch(() => { /* silent — draft save is best-effort */ });
    }, 1500);
    return () => clearTimeout(t);
  }, [form.name, form.email, form.phone, form.job_type, form.year, form.make,
      form.model, form.item_description, form.registration, form.services,
      form.notes, status]);

  // Iframe auto-resize: when this page is rendered inside a customer's site
  // (Shopify, GemPages, etc.) via <iframe>, we post the current document
  // height up to the parent so it can grow/shrink its iframe. This gets rid
  // of the internal scrollbar and the "form clipped at 640px" scaling issue.
  useEffect(() => {
    const sendHeight = () => {
      try {
        const height = document.documentElement.scrollHeight;
        window.parent?.postMessage({ type: "wrapconsole-resize", height }, "*");
      } catch (_) { /* cross-origin restrictions — nothing we can do here */ }
    };
    // Fire once immediately, then again after fonts/images settle.
    sendHeight();
    const initialTimer = setTimeout(sendHeight, 250);
    // Watch DOM size changes (validation errors, expanded fields, success
    // state, photo preview appearing, etc.).
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(sendHeight);
      observer.observe(document.body);
    }
    window.addEventListener("resize", sendHeight);
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener("resize", sendHeight);
      if (observer) observer.disconnect();
    };
  }, [status]); // re-attach on status change (idle → sending → success)

  useEffect(() => {
    // Set page + root backgrounds so the embed looks clean both standalone
    // and inside an iframe (where the host page bg should show through).
    // The base CSS in index.css locks html/body/#root to #050505; we need to
    // override all three, not just body, otherwise the dark root shows as
    // an unsightly black box around a light-themed form card.
    const light = "#F8FAFC";
    const bg = theme === "dark" ? "transparent" : light;
    const prev = {
      htmlBg: document.documentElement.style.background,
      htmlBgColor: document.documentElement.style.backgroundColor,
      bodyBg: document.body.style.background,
      bodyBgColor: document.body.style.backgroundColor,
      bodyMargin: document.body.style.margin,
    };
    const root = document.getElementById("root");
    const prevRootBg = root ? root.style.background : "";
    const prevRootBgColor = root ? root.style.backgroundColor : "";

    document.body.style.margin = "0";
    document.documentElement.style.background = bg;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.background = bg;
    document.body.style.backgroundColor = bg;
    if (root) {
      root.style.background = bg;
      root.style.backgroundColor = bg;
    }

    return () => {
      // Restore in case the SPA navigates away from the embed route
      document.documentElement.style.background = prev.htmlBg;
      document.documentElement.style.backgroundColor = prev.htmlBgColor;
      document.body.style.background = prev.bodyBg;
      document.body.style.backgroundColor = prev.bodyBgColor;
      document.body.style.margin = prev.bodyMargin;
      if (root) {
        root.style.background = prevRootBg;
        root.style.backgroundColor = prevRootBgColor;
      }
    };
  }, [theme]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const isVehicleType = (
      config?.job_types?.find((t) => t.value === form.job_type)?.is_vehicle ?? (form.job_type === "car")
    );
    const composedVehicle = isVehicleType
      ? [form.year, form.make, form.model].map((s) => (s || "").trim()).filter(Boolean).join(" ")
      : (form.item_description || "").trim();
    // Client-side required checks (server also validates)
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Name and phone number are required.");
      setStatus("error");
      return;
    }
    if (isVehicleType) {
      // Require all three parts individually — a stray "2023" with a blank
      // make/model would otherwise slip past the composed-string check.
      if (!form.year.trim() || !form.make.trim() || !form.model.trim()) {
        setError("Please add the vehicle year, make and model.");
        setStatus("error");
        return;
      }
    } else if (!composedVehicle) {
      setError("Please describe the item you want wrapped.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        job_type: form.job_type,
        year: form.year,
        make: form.make,
        model: form.model,
        item_description: form.item_description,
        vehicle: composedVehicle,           // legacy free-text mirror
        services: form.services,           // new array field
        service: form.services.join(", "),  // legacy free-text field, kept for compat
        notes: form.notes,
        hp: form.hp,
        source: params.get("source") || "embed",
        // Ties this submit back to any drafts posted from the same browser
        // session so the backend can mark them converted (and they drop
        // off the admin's Abandoned form drafts panel).
        session_id: sessionIdRef.current,
      };
      // Only include email if the user actually typed one — Pydantic's
      // EmailStr rejects blank strings, so omit the key otherwise.
      if (form.email && form.email.trim()) payload.email = form.email.trim();
      if (form.registration && form.registration.trim()) payload.registration = form.registration.trim();
      if (form.photo_base64) {
        payload.photo_base64 = form.photo_base64;
        payload.photo_filename = form.photo_filename || "vehicle.jpg";
      }
      await axios.post(`${API_BASE}/public/leads`, payload);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      // Handle FastAPI validation payloads (array of {msg,loc,...}) gracefully
      if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg).join(", ") || "Please check the form and try again.");
      } else {
        setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    }
  };

  // Compress the picked image to <=1600px on the longest edge, JPEG q≈0.82.
  // Keeps upload payloads small even from phone cameras.
  const handlePhotoPick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please pick an image file (JPG, PNG, HEIC, etc.)");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("Photo is too large — please pick something under 20 MB.");
      return;
    }
    setError("");
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(file);
      });
      // Compress with a canvas
      const img = new Image();
      const compressed = await new Promise((resolve, reject) => {
        img.onload = () => {
          const maxDim = 1600;
          let { width: w, height: h } = img;
          if (w > maxDim || h > maxDim) {
            if (w >= h) { h = Math.round(h * maxDim / w); w = maxDim; }
            else { w = Math.round(w * maxDim / h); h = maxDim; }
          }
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.onerror = () => reject(new Error("Could not read image"));
        img.src = dataUrl;
      });
      setForm((f) => ({
        ...f,
        photo_base64: compressed,
        photo_filename: (file.name || "vehicle.jpg").replace(/\.[^.]+$/, "") + ".jpg",
        photo_preview: compressed,
      }));
    } catch (e) {
      setError("Couldn't process that image. Please try another one.");
    }
  };

  const clearPhoto = () => setForm((f) => ({ ...f, photo_base64: "", photo_filename: "", photo_preview: "" }));

  const dark = theme === "dark";
  const surface = dark ? "bg-white/[0.03] border-white/10 backdrop-blur-xl" : "bg-white border-slate-200";
  const text = dark ? "text-white" : "text-slate-900";
  const subtext = dark ? "text-slate-400" : "text-slate-500";
  const label = dark ? "text-slate-400" : "text-slate-500";
  const input = dark
    ? "bg-black/40 border-white/10 text-white placeholder-slate-500 focus:border-white/30"
    : "bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-slate-400";

  if (status === "success") {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${dark ? "grain" : ""}`}>
        <div className={`w-full max-w-md rounded-3xl border ${surface} p-8 text-center space-y-4`}>
          <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: "#10B981" }} />
          <h1 className={`font-heading text-2xl ${text}`}>Thanks — we got it.</h1>
          <p className={`text-sm ${subtext}`}>
            One of our team will get back to you shortly with a personalized quote.
          </p>
          <button
            onClick={() => { setForm({ name: "", email: "", phone: "", job_type: "car", year: "", make: "", model: "", item_description: "", vehicle: "", registration: "", services: [], notes: "", photo_base64: "", photo_filename: "", photo_preview: "", hp: "" }); setStatus("idle"); }}
            data-testid="embed-submit-another"
            className="text-xs px-4 py-2 rounded-full border border-current opacity-70 hover:opacity-100 transition"
            style={{ color: accent }}
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 md:p-8 ${dark ? "grain" : ""} ${text}`}>
      <div className={`w-full max-w-lg mx-auto rounded-3xl border ${surface} p-6 md:p-8 space-y-5`}>
        {config && (
          <div>
            <div className={`text-[10px] uppercase tracking-[0.25em] ${label}`}>
              {config.company_name}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-light tracking-tight mt-1">
              {config.tagline || "Get a free wrap quote"}
            </h1>
            <p className={`text-sm mt-2 ${subtext}`}>
              Fill this out and we&apos;ll get back to you within a few hours.
            </p>
          </div>
        )}

        <form onSubmit={submit} noValidate className="space-y-3" data-testid="embed-lead-form">
          {/* Honeypot */}
          <input
            type="text"
            name="hp"
            tabIndex={-1}
            autoComplete="off"
            value={form.hp}
            onChange={(e) => setForm({ ...form, hp: e.target.value })}
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
            aria-hidden="true"
          />

          <FieldEmbed labelText="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })}
            label={label} input={input} testId="embed-name" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldEmbed labelText="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })}
              label={label} input={input} testId="embed-email" />
            <FieldEmbed labelText="Phone" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
              label={label} input={input} testId="embed-phone" />
          </div>

          {/* What type of item — mirrors the Item picker used in the CRM
              (car, boat, caravan, motorbike, etc.). Toggles year/make/model
              vs a single item-description field beneath. */}
          <div>
            <div className={`text-[10px] uppercase tracking-[0.2em] mb-1.5 ${label}`}>What are you getting wrapped? *</div>
            <select
              data-testid="embed-job-type"
              value={form.job_type}
              onChange={(e) => setForm({ ...form, job_type: e.target.value })}
              className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none ${input}`}
            >
              {(config?.job_types || [{ value: "car", label: "Car / Vehicle", is_vehicle: true }]).map((t) => (
                <option key={t.value} value={t.value} className={dark ? "bg-neutral-900" : ""}>{t.label}</option>
              ))}
            </select>
          </div>

          {(() => {
            const isVehicle = (
              config?.job_types?.find((t) => t.value === form.job_type)?.is_vehicle ?? (form.job_type === "car")
            );
            if (isVehicle) {
              return (
                <div className="grid grid-cols-3 gap-3" data-testid="embed-vehicle-fields">
                  <FieldEmbed labelText="Year" required value={form.year}
                    onChange={(v) => setForm({ ...form, year: v })}
                    placeholder="2023"
                    label={label} input={input} testId="embed-year" />
                  <FieldEmbed labelText="Make" required value={form.make}
                    onChange={(v) => setForm({ ...form, make: v })}
                    placeholder="Tesla"
                    label={label} input={input} testId="embed-make" />
                  <FieldEmbed labelText="Model" required value={form.model}
                    onChange={(v) => setForm({ ...form, model: v })}
                    placeholder="Model 3"
                    label={label} input={input} testId="embed-model" />
                </div>
              );
            }
            return (
              <FieldEmbed labelText="Item description" required value={form.item_description}
                onChange={(v) => setForm({ ...form, item_description: v })}
                placeholder="e.g. Samsung double-door fridge · 3m × 1m illuminated sign"
                label={label} input={input} testId="embed-item-description" />
            );
          })()}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldEmbed labelText="Registration (optional)" value={form.registration}
              onChange={(v) => setForm({ ...form, registration: v.toUpperCase() })}
              placeholder="e.g. ABC-123"
              label={label} input={input} testId="embed-registration" />
            <div>
              <div className={`text-[10px] uppercase tracking-[0.2em] mb-1.5 ${label}`}>Vehicle photo (optional)</div>
              {form.photo_preview ? (
                <div className="flex items-center gap-2">
                  <img src={form.photo_preview} alt="preview" className="w-16 h-16 rounded-xl object-cover border border-white/10" data-testid="embed-photo-preview" />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs truncate ${text}`}>{form.photo_filename}</div>
                    <button
                      type="button"
                      data-testid="embed-photo-remove"
                      onClick={clearPhoto}
                      className={`text-[11px] mt-1 underline decoration-dotted ${subtext} hover:${text}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  className={`flex items-center justify-center gap-2 cursor-pointer rounded-xl border border-dashed py-2.5 text-sm transition ${
                    dark ? "bg-white/[0.02] border-white/15 text-slate-300 hover:bg-white/[0.05]" : "bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid="embed-photo-picker"
                >
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="embed-photo-input"
                    className="hidden"
                    onChange={(e) => handlePhotoPick(e.target.files?.[0])}
                  />
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h4l2-2h4l2 2h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><circle cx="12" cy="13" r="4"/></svg>
                  <span>Add a photo</span>
                </label>
              )}
            </div>
          </div>

          <div>
            <div className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${label}`}>Service interest (tick all that apply)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="embed-service-grid">
              {(config?.services || []).map((s) => {
                const active = form.services.includes(s);
                return (
                  <label
                    key={s}
                    data-testid={`embed-service-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`flex items-center gap-2 cursor-pointer rounded-xl border px-3 py-2.5 text-sm transition select-none ${
                      active
                        ? (dark ? "bg-white/[0.08] border-white/30 text-white" : "bg-slate-900 border-slate-900 text-white")
                        : (dark ? "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]" : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100")
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => {
                        const next = active
                          ? form.services.filter((x) => x !== s)
                          : [...form.services, s];
                        setForm({ ...form, services: next });
                      }}
                      className="w-4 h-4 accent-current shrink-0"
                    />
                    <span className="truncate">{s}</span>
                  </label>
                );
              })}
              {(!config?.services || config.services.length === 0) && (
                <div className={`text-xs italic ${subtext}`}>No services configured yet.</div>
              )}
            </div>
          </div>

          <div>
            <div className={`text-[10px] uppercase tracking-[0.2em] mb-1.5 ${label}`}>Additional details</div>
            <textarea
              data-testid="embed-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Color preferences, deadlines, questions…"
              className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none ${input}`}
            />
          </div>

          {error && (
            <div data-testid="embed-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="embed-submit"
            disabled={status === "sending"}
            className="w-full font-semibold rounded-full py-3 hover:opacity-90 transition disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: accent, color: theme === "dark" ? "#fff" : "#fff" }}
          >
            {status === "sending" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Request my quote"}
          </button>

          <p className={`text-[10px] ${subtext} text-center pt-2 flex items-center justify-center gap-1`}>
            <Sparkles className="w-3 h-3" /> Powered by {config?.company_name || "WrapConsole"}
          </p>
        </form>
      </div>
    </div>
  );
}

function FieldEmbed({ labelText, value, onChange, type = "text", required = false, placeholder = "", label, input, testId }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-[0.2em] mb-1.5 ${label}`}>{labelText}{required && " *"}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none ${input}`}
      />
    </div>
  );
}
